var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Zod from 'zod';
import { objectivePrefix, operationPrefix, } from '../agent/index';
import { chunkSchema, getSubSchema } from '../utils/openapi/chunk-schema';
import { deepenSchema, shallowSchema } from '../utils/openapi/deepen-schema';
import { mergeSchema } from '../utils/openapi/merge-schema';
import { t } from '../utils/template';
export default class ContextProcessor {
    constructor(parser) {
        this.parser = parser;
        this.logger = parser.logger;
    }
    filter(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const contextShortlist = Array.from(new Set([
                ...(yield this.shortlist(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.body }))),
                ...(yield this.shortlist(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.query }))),
                ...(yield this.shortlist(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.path }))),
            ]));
            const filteredContextForBody = yield this.filterSchema(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.body, contextShortlist }));
            const filteredContextForQuery = yield this.filterSchema(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.query, contextShortlist }));
            const filteredContextForPath = yield this.filterSchema(Object.assign(Object.assign({}, params), { inputSchema: params.inputSchema.path, contextShortlist }));
            return {
                filteredContextForBody,
                filteredContextForQuery,
                filteredContextForPath,
            };
        });
    }
    filterSchema(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const filteredContext = {};
            for (const key of params.contextShortlist) {
                let filteredSchema = shallowSchema(params.context[key]);
                const chunks = chunkSchema(params.context[key], true);
                for (const { schema: chunkSchema, propertyNames } of chunks) {
                    if (!propertyNames.length) {
                        filteredSchema = mergeSchema(filteredSchema, chunkSchema);
                        continue;
                    }
                    const { shortlist } = yield this.parser.llm.generateStructured({
                        messages: [
                            {
                                role: 'user',
                                content: t([
                                    objectivePrefix(params, false),
                                    operationPrefix(params),
                                    'And I came up with this input JSON schema to the resource:',
                                    '```{{inputSchema}}```',
                                    'Here is a JSON schema of a variable named `{{key}}`:',
                                    '```{{chunkSchema}}```',
                                    "Your task is to shortlist a conservative set of properties from {{key}}'s" +
                                        ' JSON schema which may be relevant to generate an input compliant to the input schema.',
                                ], {
                                    inputSchema: JSON.stringify(params.inputSchema),
                                    key,
                                    chunkSchema: JSON.stringify(chunkSchema),
                                }),
                            },
                        ],
                        generatorName: 'shortlist_properties',
                        generatorDescription: 'Shortlist properties that are relevant given the objective',
                        generatorOutputSchema: Zod.object({
                            shortlist: Zod.array(Zod.enum(propertyNames)),
                        }),
                    });
                    const subSchema = getSubSchema(chunkSchema, shortlist);
                    filteredSchema = mergeSchema(filteredSchema, subSchema);
                }
                filteredContext[key] = deepenSchema(params.context[key], filteredSchema);
            }
            return filteredContext;
        });
    }
    shortlist(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (Object.keys(params.context).length === 0) {
                return [];
            }
            const { shortlist } = yield this.parser.llm.generateStructured({
                messages: [
                    {
                        role: 'user',
                        content: t([
                            objectivePrefix(params),
                            operationPrefix(params),
                            'And this is the input JSON schema to the resource:',
                            '```{{inputSchema}}```',
                        ], {
                            description: params.description,
                            inputSchema: JSON.stringify(params.inputSchema),
                        }),
                    },
                ],
                generatorName: 'shortlist_context',
                generatorDescription: 'Shortlist context datum that are relevant given the objective',
                generatorOutputSchema: Zod.object({
                    shortlist: Zod.array(Zod.enum(Object.keys(params.context))),
                }),
            });
            return shortlist;
        });
    }
}
